import React, { useEffect, useState } from 'react'
import { MotifButton, MotifFormField, MotifIcon, MotifModal, MotifRadioButton, MotifTextArea, MotifTooltip } from '@ey-xd/motif-react'
import './UserFeedbackButtons.scss'
import { IconoirThumbsUp, IconoirThumbsDown } from '@ey-xd/motif-icon'

type UserFeedbackButtonsProps = {
  referenceType: number
  onFeedbackSubmit: (feedback: { type: number; option: number | null; text: string; referenceId: string, referenceType: number }) => void
}

const feedbackOptions = [  
  { value: 2, label: 'Partially correct output' },
  { value: 3, label: 'Completely wrong output' },
  { value: 4, label: 'Other' },
]

export const UserFeedbackButtons: React.FC<UserFeedbackButtonsProps> = ({ referenceType, onFeedbackSubmit }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedButton, setSelectedButton] = useState<'up' | 'down' | null>(null)
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [text, setText] = useState('')
  const [hideTooltip, setHideTooltip] = useState(false)

  const handleThumbClick = (button: 'up' | 'down') => {
    setSelectedButton(button)
    setHideTooltip(true)
    setDialogOpen(true)    
  }

  useEffect(() => {
    if (selectedButton === 'up') {
      if (selectedOption !== 1) {
        handleOptionChange(1)
      }
    }
  }, [selectedButton])

  const handleOptionChange = (value: number) => {
    setSelectedOption(value)
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
  }

  const handleClose = () => {
    setDialogOpen(false)
    setSelectedOption(null)
    setText('')
    setSelectedButton(null)
    setHideTooltip(false)
  }

  const handleSubmit = () => {
    // Record feedback here (send to API, etc.)
    if (selectedButton === null) {
      return
    }
    const type = selectedButton === 'up' ? 1 : 0
    onFeedbackSubmit({ type, option: selectedOption, text, referenceId: '', referenceType })
    handleClose()
  }

  return (
    <div className="user-feedback-widget">
      <MotifTooltip
        allowClick
        allowHover
        closeButtonProps={{}}
        contentClassName=""
        flip
        forceHideCounter={0}
        icons={{}}
        id="tooltip"
        offset={[
            0,
            8
        ]}
        placement="auto"
        style={{}}
        trigger={
          <MotifButton
            aria-label="Thumbs up"
            variant={selectedButton === 'up' ? 'primary' : 'secondary'}
            onClick={() => handleThumbClick('up')}
            size="small"
            title='I like something'
          >
            <MotifIcon iconFunction={IconoirThumbsUp} />
        </MotifButton>}
        variant="default"
        hide={hideTooltip}
      >
        I like something
      </MotifTooltip>
      <MotifTooltip
        allowClick
        allowHover
        closeButtonProps={{}}
        contentClassName=""
        flip
        forceHideCounter={0}
        icons={{}}
        id="tooltip"
        offset={[
            0,
            8
        ]}
        placement="auto"
        style={{}}
        trigger={
            <MotifButton
                aria-label="Thumbs down"
                variant={selectedButton === 'down' ? 'primary' : 'secondary'}
                onClick={() => handleThumbClick('down')}
                size="small"
                title='I don&quote;t like something'
            >
                <MotifIcon iconFunction={IconoirThumbsDown} />
            </MotifButton>}
        variant="default"
        hide={hideTooltip}
        >
        I don't like something
    </MotifTooltip>
      <MotifModal
        show={dialogOpen}
        onClose={handleClose}
        size="sm"
        className="user-feedback-dialog"
      >
        <div className="user-feedback-dialog-content">
          <div className="user-feedback-title">
            {selectedButton === 'up' ? 'Please help us improve' : 'What can we improve?'}
          </div>
          <div className="user-feedback-options">
            {selectedButton === 'up' ? (
              // For thumbs up, automatically set option to 1 and don't show radio buttons
              <></>
            ) : (
              feedbackOptions.map(option => (
              <MotifRadioButton
                key={option.value}
                value={option.label}
                checked={selectedOption === option.value}
                onChange={() => handleOptionChange(option.value)}
                name="feedback-radio"
              >
                {option.label}
              </MotifRadioButton>
            ))
          )}
          </div>
          <MotifFormField
            aria-label="Label"
            role="group"
            >
            <MotifTextArea
                placeholder={selectedButton === 'up' ? 'What did you like? What enhancements can be made? Do not include any private or sensitive information.' : 'What were the shortcomings in the response, and what enhancements can be made? Do not include any private or sensitive information.'}
                value={text}
                aria-label="User feedback text area"
                onChange={handleTextChange}
                rows={3}
                className="user-feedback-textarea"
                disabled={selectedOption === null}
                name="user-feedback-textarea"
                characterLimit={2000}
                hintText="You can provide additional feedback here."
                showCounter={true}
                errorMessage='Please enter a valid comment.'
            />
          </MotifFormField>
          <div className="user-feedback-actions">
            <MotifButton
              variant="secondary"
              onClick={handleClose}
              size="small"
            >
              Skip
            </MotifButton>
            <MotifButton
              variant="primary"
              onClick={handleSubmit}
              disabled={selectedOption === null}
              size="small"
            >
              Submit feedback
            </MotifButton>
          </div>
        </div>
      </MotifModal>
    </div>
  )
}

export default UserFeedbackButtons